import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, Heart, Share2, MessageCircle, Phone } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card.tsx";
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts } from '../rtk/slices/products-slice.js';

import Zoom from 'react-medium-image-zoom'
import 'react-medium-image-zoom/dist/styles.css'
import FullPageFloatingElements from '../components/FloatingBackground.jsx';


const ProductDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { products, loading, status, error } = useSelector((state) => state.products);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (!products || products.length === 0) {
      dispatch(fetchProducts());
    }
  }, [dispatch, products]);

  const product = useMemo(() => {
    if (products && products.length > 0) {
      return products.find(p => p.id == id);
    }
    return undefined;
  }, [products, id]);


  if ((status === 'loading' || status === 'idle' || status === "failed") && !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">{t('product.error', 'Error Loading Product')}</h2>
          <p className="text-gray-500 mb-6">{typeof error === 'string' ? error : 'An unexpected error occurred.'}</p>
          <Button onClick={() => navigate('/products')}>{t('product.backToProducts', 'Back to Products')}</Button>
        </div>
      </div>
    );
  }

  if (!product && status === "succeeded") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('product.notFound', 'Product Not Found')}</h2>
          <p className="text-gray-500 mb-6">
            {t('product.notExistOrMoved', 'The product you are looking for does not exist or has been moved.')}
          </p>
          <Button onClick={() => navigate('/products')}>
            {t('product.backToProducts', 'Back to Products')}
          </Button>
        </div>
      </div>
    );
  }

  const currentLanguage = i18n.language;
  const title = product[`name_${currentLanguage}`] || product.name_en;
  const description = product[`description_${currentLanguage}`] || product.description_en;
  const specificationsRaw = product[`specifications_${currentLanguage}`] || product.specifications_en || "";
  const specifications = specificationsRaw.split(/[\r\n]+/).filter(line => line.trim() !== '');

  const baseUrl = process.env.REACT_APP_PROJECTS_API || '';
  const productImages = [
    product.img,
    product.side_img1,
    product.side_img2,
    product.side_img3,
    product.side_img4
  ].filter(img => img).map(img => `${baseUrl}assest/imgs/${img}`);

  const handleWhatsAppContact = () => {
    const message = encodeURIComponent(`${t('whatsapp.hello')} ${title}. ${t('whatsapp.interested')}`);
    const phoneNumber = "+201227316680";
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  const handlePhoneContact = () => {
    window.open('tel:+201227316680', '_self');
  };

  return (
    <div className="relative min-h-screen bg-gray-50 z-10">
      {/* <FullPageFloatingElements /> */}

      <div className="pt-20 pb-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 mb-8 text-sm">
            <Link to="/" className="text-amber-600 hover:text-amber-700">{t('nav.home')}</Link>
            <span className="text-gray-400">/</span>
            <Link to="/products" className="text-amber-600 hover:text-amber-700">{t('nav.products')}</Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-600 line-clamp-1">{title}</span>
          </div>

          <Button onClick={() => navigate('/products')} variant="ghost" className="gap-2 mb-6">
            <ArrowLeft className="w-4 h-4" />
            {t('product.backToProducts')}
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div className="space-y-4">
              <motion.div
                key={selectedImageIndex}
                initial={{ opacity: 0.8 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="relative overflow-hidden rounded-lg shadow-lg"
              >
                <Zoom>
                  <img
                    src={productImages.length > 0 ? productImages[selectedImageIndex] : '/path/to/placeholder.jpg'}
                    alt={title}
                    className="w-full h-96 lg:h-[500px] object-cover zoomProduct cursor-zoom-in"
                    style={{ cursor: 'zoom-in !important' }}
                  />
                </Zoom>
                <div className="absolute top-4 right-4 flex gap-2">
                  <Button size="icon" variant="outline" className="bg-white/80 rounded-full h-9 w-9"><Heart className="w-4 h-4" /></Button>
                  <Button size="icon" variant="outline" className="bg-white/80 rounded-full h-9 w-9"><Share2 className="w-4 h-4" /></Button>
                </div>
              </motion.div>


              {productImages.length > 1 && (
                <div className="grid grid-cols-5 gap-2">
                  {productImages.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`relative overflow-hidden rounded-lg border-2 transition-all ${selectedImageIndex === index ? 'border-amber-500' : 'border-transparent hover:border-amber-300'}`}
                    >
                      <img src={image} alt={`${title} thumbnail ${index + 1}`} className="w-full h-20 object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div>
                <h1 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">{title}</h1>
                <div className="text-3xl font-bold text-amber-600 mb-6">${product.price}</div>
              </div>

              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('product.description')}</h3>
                  <p className="text-gray-600 leading-relaxed">{description}</p>
                </CardContent>
              </Card>

              {specifications.length > 0 && specifications[0] !== '' && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('product.specifications')}</h3>
                    <ul className="space-y-2">
                      {specifications.map((spec, index) => (
                        <li key={index} className="flex items-start gap-3 text-gray-600">
                          <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 shrink-0"></div>
                          <span>{spec}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}

              <div className="space-y-4 pt-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  {t('contact.interestedTitle', 'Interested in this product?')}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Button
                    onClick={handleWhatsAppContact}
                    className="bg-green-500 hover:bg-green-600 text-white gap-2 py-3"
                    size="lg"
                  >
                    <MessageCircle className="w-5 h-5" />
                    {t('contact.whatsapp')}
                  </Button>
                  <Button
                    onClick={handlePhoneContact}
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 gap-2 py-3"
                    size="lg"
                  >
                    <Phone className="w-5 h-5" />
                    {t('contact.call')}
                  </Button>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-amber-800 text-sm">
                    <strong>{t('contact.note')}:</strong> {t('contact.noteText')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
